/**
 * Cookie Service for CuriousJr Web Revamp
 * Handles cookie management with type safety
 */

export enum CookieKeys {
  TOKEN_CONTEXT = 'TOKEN_CONTEXT',
  USER_PREFERENCES = 'USER_PREFERENCES',
  THEME = 'THEME',
  LANGUAGE = 'LANGUAGE',
}

export const getDomainName = () => {
  let hostname = window.location.hostname;
  if (hostname.startsWith('www.')) {
    hostname = hostname.slice(4); // remove 'www.'
  }
  if (hostname === 'localhost' || /^[0-9.]+$/.test(hostname)) {
    return hostname;
  }
  const parts = hostname.split('.');
  if (parts.length >= 3 && parts.slice(-2).join('.').length <= 6) {
    // Handle special TLDs like co.in, gov.uk
    return '.' + parts.slice(-3).join('.');
  } else {
    return '.' + parts.slice(-2).join('.');
  }
};

export interface TokenContext {
  token: string;
  refreshToken?: string;
  expiresAt?: number;
  userId?: string;
}

export class CookieService {
  static getValue(key: CookieKeys): any {
    if (typeof window === 'undefined') return null;

    try {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${key}=`);
      if (parts.length === 2) {
        const cookieValue = parts.pop()?.split(';').shift();
        if (cookieValue) {
          try {
            return JSON.parse(decodeURIComponent(cookieValue));
          } catch {
            return cookieValue;
          }
        }
      }
      return null;
    } catch (error) {
      console.warn(`Failed to get cookie '${key}':`, error);
      return null;
    }
  }

  static deleteAllCookies() {
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      document.cookie = cookies[i] + '=; expires=' + new Date(0).toUTCString();
    }
  }

  static setValue(
    key: CookieKeys,
    value: any,
    options: {
      expires?: Date;
      maxAge?: number;
      path?: string;
      domain?: string;
      secure?: boolean;
      httpOnly?: boolean;
      sameSite?: 'strict' | 'lax' | 'none';
    } = {}
  ): void {
    if (typeof window === 'undefined') return;

    try {
      const serializedValue =
        typeof value === 'object'
          ? encodeURIComponent(JSON.stringify(value))
          : encodeURIComponent(String(value));

      let cookieString = `${key}=${serializedValue}`;

      if (options.expires) {
        cookieString += `; expires=${options.expires.toUTCString()}`;
      }

      if (options.maxAge !== undefined) {
        cookieString += `; max-age=${options.maxAge}`;
      }

      if (options.path) {
        cookieString += `; path=${options.path}`;
      } else {
        cookieString += `; path=/`;
      }

      if (options.domain) {
        cookieString += `; domain=${options.domain}`;
      }

      if (options.secure) {
        cookieString += `; secure`;
      }

      if (options.sameSite) {
        cookieString += `; samesite=${options.sameSite}`;
      }

      document.cookie = cookieString;
    } catch (error) {
      console.warn(`Failed to set cookie '${key}':`, error);
    }
  }

  static removeValue(key: CookieKeys): void {
    if (typeof window === 'undefined') return;

    try {
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;domain=${getDomainName()}`;
    } catch (error) {
      console.warn(`Failed to remove cookie '${key}':`, error);
    }
  }

  static clear(): void {
    if (typeof window === 'undefined') return;

    Object.values(CookieKeys).forEach(key => {
      this.removeValue(key);
    });
  }
}
